import {
  type ReactElement,
  type SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { Link } from "react-router";

import { LinkRow } from "@/components/LinkRow";
import { PageHeading } from "@/components/PageHeading";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LOAD_FAILED, useResource } from "@/lib/api";
import {
  type CellCreateRequest,
  type PlatformCell,
  type PlatformControl,
  type ProvisioningReport,
  createCell,
  isMigratable,
  loadCellRevision,
  loadCells,
  loadControl,
  migrateCell,
  migrateControl,
} from "@/lib/platform";
import {
  CELL_LOCATION_LABELS,
  DOMAIN_PURPOSE_LABELS,
  OPERATING_MODEL_LABELS,
  PROVISIONING_STATE_LABELS,
  TENANT_STATUS_LABELS,
} from "@/platform/labels";
import {
  INPUT,
  ReportTable,
  options,
  revisionText,
} from "@/platform/shared";

/**
 * The cells and the control plane: what a deploy leaves behind to check and migrate.
 *
 * The list comes from the control plane alone; each cell's revision is one request of its
 * own, so a cell whose database does not answer leaves its row empty, not the page.
 *
 * "Alle migrieren" is a loop here, one request per cell, in the order of the list, and it
 * stops at the first failure like `cell migrate` does. No request runs longer than one cell,
 * and the error names the cell it happened in.
 */
export function CellsPage(): ReactElement {
  const cells = useResource<PlatformCell[]>(loadCells);
  const control = useResource<PlatformControl>(loadControl);
  const [revisions, setRevisions] = useState<Record<string, string | null>>(
    {},
  );
  const [busy, setBusy] = useState(false);
  const [migrationMessage, setMigrationMessage] = useState<string | null>(
    null,
  );
  const [createMessage, setCreateMessage] = useState<string | null>(null);
  const [report, setReport] = useState<ProvisioningReport | null>(null);

  const cellList = cells.status === "ready" ? cells.data : undefined;
  const controlView = control.status === "ready" ? control.data : undefined;
  const migratable = cellList?.filter(isMigratable) ?? [];

  useEffect(() => {
    if (cellList === undefined) {
      return;
    }
    let active = true;
    for (const cell of cellList.filter(isMigratable)) {
      void loadCellRevision(cell.slug).then((revision) => {
        if (active) {
          setRevisions((known) => ({ ...known, [cell.slug]: revision }));
        }
      });
    }
    return () => {
      active = false;
    };
  }, [cellList]);

  async function migrateAll(): Promise<void> {
    setBusy(true);
    try {
      for (const cell of migratable) {
        setMigrationMessage(`${cell.slug} wird migriert …`);
        const outcome = await migrateCell(cell.slug);
        if (!outcome.ok) {
          setMigrationMessage(`${cell.slug}: ${outcome.message}`);
          return;
        }
        setRevisions((known) => ({
          ...known,
          [cell.slug]: outcome.data.revision,
        }));
      }
      setMigrationMessage("Alle Zellen migriert.");
    } catch (error: unknown) {
      console.error("[CellsPage] migrate all failed", error);
      setMigrationMessage(LOAD_FAILED);
    } finally {
      setBusy(false);
    }
  }

  async function migrateControlPlane(): Promise<void> {
    setBusy(true);
    setMigrationMessage("Steuerebene wird migriert …");
    try {
      const outcome = await migrateControl();
      setMigrationMessage(
        outcome.ok ? "Steuerebene migriert." : outcome.message,
      );
      if (outcome.ok) {
        control.reload();
      }
    } catch (error: unknown) {
      console.error("[CellsPage] control migrate failed", error);
      setMigrationMessage(LOAD_FAILED);
    } finally {
      setBusy(false);
    }
  }

  async function create(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    const text = (name: string): string =>
      String(fields.get(name) ?? "").trim();
    setBusy(true);
    setCreateMessage(null);
    setReport(null);
    try {
      const outcome = await createCell({
        slug: text("slug"),
        name: text("name"),
        hostname: text("hostname") || null,
        // The selects offer the label maps' keys only.
        purpose: text("purpose") as CellCreateRequest["purpose"],
        operating_model: text(
          "operating_model",
        ) as CellCreateRequest["operating_model"],
        location: text("location") as CellCreateRequest["location"],
        identity_realm: text("identity_realm") || null,
      });
      if (outcome.ok) {
        console.log("[CellsPage] cell created", outcome.data.slug);
        setReport(outcome.data);
        form.reset();
        cells.reload();
      } else {
        setCreateMessage(outcome.message);
      }
    } catch (error: unknown) {
      console.error("[CellsPage] create failed", error);
      setCreateMessage(LOAD_FAILED);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeading title="Zellen" />

      {cells.status === "loading" ? (
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      ) : null}
      {cells.status !== "loading" && cellList === undefined ? (
        <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
      ) : null}
      {cellList !== undefined ? (
        <Table>
          <TableCaption className="sr-only">Zellen</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Kurzname</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Einrichtung</TableHead>
              <TableHead>Revision</TableHead>
              <TableHead>Domains</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cellList.map((cell) => (
              <LinkRow key={cell.slug}>
                <TableCell className="font-medium">
                  <Link
                    className="underline underline-offset-4"
                    to={`/zellen/${encodeURIComponent(cell.slug)}`}
                  >
                    {cell.slug}
                  </Link>
                </TableCell>
                <TableCell>{cell.name}</TableCell>
                <TableCell>{TENANT_STATUS_LABELS[cell.status]}</TableCell>
                <TableCell>
                  {PROVISIONING_STATE_LABELS[cell.provisioning_state]}
                </TableCell>
                <TableCell>
                  {isMigratable(cell)
                    ? revisionText(revisions[cell.slug], controlView?.cell_head)
                    : null}
                </TableCell>
                <TableCell>{cell.hostnames.length}</TableCell>
              </LinkRow>
            ))}
            {cellList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground">
                  Keine Zellen.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      ) : null}

      <div className="mt-4">
        <Button
          type="button"
          disabled={busy || migratable.length === 0}
          onClick={() => {
            void migrateAll();
          }}
        >
          Alle migrieren
        </Button>
      </div>

      <section className="mt-8">
        <h2 className="text-lg font-bold">
          Steuerebene
        </h2>
        {controlView === undefined ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {control.status === "loading" ? "Wird geladen …" : LOAD_FAILED}
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
            <span>
              Revision{" "}
              {revisionText(
                controlView.control_revision,
                controlView.control_head,
              )}
            </span>
            {controlView.control_revision !== controlView.control_head ? (
              <Button
                type="button"
                disabled={busy}
                onClick={() => {
                  void migrateControlPlane();
                }}
              >
                Migrieren
              </Button>
            ) : null}
          </div>
        )}
      </section>

      {migrationMessage !== null ? (
        <p
          className="mt-4 border border-border bg-surface-sunken px-4 py-3 text-sm"
          role="status"
          aria-live="polite"
        >
          {migrationMessage}
        </p>
      ) : null}

      <details className="mt-8 max-w-2xl">
        <summary className="cursor-pointer font-semibold">
          Zelle anlegen
        </summary>
        <form
          className="mt-4 grid gap-4"
          onSubmit={(event) => {
            void create(event);
          }}
        >
          <div>
            <label className="block text-sm font-medium" htmlFor="zelle-kurzname">
              Kurzname
            </label>
            <input
              className={INPUT}
              id="zelle-kurzname"
              name="slug"
              required
              pattern="[a-z][a-z0-9_]{1,30}"
              maxLength={31}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="zelle-name">
              Name
            </label>
            <input
              className={INPUT}
              id="zelle-name"
              name="name"
              required
              maxLength={200}
            />
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="zelle-domain">
              Domain
            </label>
            <input
              className={INPUT}
              id="zelle-domain"
              name="hostname"
              maxLength={253}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="zelle-zweck">
              Zweck der Domain
            </label>
            <select
              className={INPUT}
              id="zelle-zweck"
              name="purpose"
              defaultValue="participant"
            >
              {options(DOMAIN_PURPOSE_LABELS)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="zelle-modell">
              Betriebsmodell
            </label>
            <select
              className={INPUT}
              id="zelle-modell"
              name="operating_model"
              defaultValue="m3_managed"
            >
              {options(OPERATING_MODEL_LABELS)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="zelle-standort">
              Standort
            </label>
            <select
              className={INPUT}
              id="zelle-standort"
              name="location"
              defaultValue="s1_operator"
            >
              {options(CELL_LOCATION_LABELS)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="zelle-raum">
              Identitätsraum
            </label>
            <input
              className={INPUT}
              id="zelle-raum"
              name="identity_realm"
              maxLength={200}
              autoComplete="off"
            />
          </div>
          <div>
            <Button type="submit" disabled={busy}>
              Anlegen
            </Button>
          </div>
        </form>

        {createMessage !== null ? (
          <p
            className="mt-4 border border-border bg-surface-sunken px-4 py-3 text-sm"
            role="status"
            aria-live="polite"
          >
            {createMessage}
          </p>
        ) : null}

        {report !== null ? <ReportTable report={report} /> : null}
      </details>
    </>
  );
}
